/**
 * Report Builder - Place Image JavaScript
 *
 * Manages image placement on the report canvas
 */

(function () {
  "use strict";

  // Store placed images
  let placedImages = [];
  let imageCounter = 0;
  let isAddingImagePlaceholder = false;
  let imageMediaFrame = null;

  /**
   * Initialize the place image functionality
   */
  function initPlaceImage() {
    // This will be called from the toolbar button click
  }

  /**
   * Initialize WordPress media library frame
   */
  function initMediaFrame() {
    if (imageMediaFrame) {
      return imageMediaFrame;
    }

    if (typeof wp === "undefined" || typeof wp.media === "undefined") {
      console.error("WordPress media library is not available");
      return null;
    }

    // Create the media frame
    imageMediaFrame = wp.media({
      title: "Select or Upload Image",
      button: {
        text: "Use this image",
      },
      library: {
        type: "image", // Only allow images
      },
      multiple: false, // Only allow single image selection
    });

    return imageMediaFrame;
  }

  /**
   * Add image placeholder to canvas with inline form
   */
  function addImagePlaceholder() {
    // Prevent duplicate calls
    if (isAddingImagePlaceholder) {
      return;
    }

    isAddingImagePlaceholder = true;

    const canvasCharts = window.ReportBuilderUtils?.getCanvasContainer();
    if (!canvasCharts) {
      isAddingImagePlaceholder = false;
      return;
    }

    // Hide empty message
    if (window.ReportBuilderUtils?.toggleEmptyMessage) {
      window.ReportBuilderUtils.toggleEmptyMessage(false);
    }

    // Create placeholder container
    const placeholderContainer = document.createElement("div");
    placeholderContainer.className = "canvas-image-placeholder";
    placeholderContainer.id = `image_placeholder_${Date.now()}`;

    // Clone the inline form template
    const inlineFormTemplate = document.querySelector(
      ".report-builder-place-image-inline"
    );
    if (!inlineFormTemplate) {
      console.error("Inline image form template not found");
      isAddingImagePlaceholder = false;
      return;
    }

    const inlineForm = inlineFormTemplate.cloneNode(true);
    inlineForm.style.display = "block";
    inlineForm.id = `inline_image_form_${Date.now()}`;

    // Update IDs to be unique
    const imageIdInput = inlineForm.querySelector("#inlineSelectImage");
    const imageSelectBtn = inlineForm.querySelector("#inlineImageSelectBtn");
    const imagePreview = inlineForm.querySelector("#inlineImagePreview");
    const selectImageSize = inlineForm.querySelector("#inlineSelectImageSize");
    const addImageBtn = inlineForm.querySelector("#inlineAddImageBtn");

    const uniqueId = Date.now();

    if (imageIdInput) {
      imageIdInput.id = `inlineSelectImage_${uniqueId}`;
    }
    if (imageSelectBtn) {
      imageSelectBtn.id = `inlineImageSelectBtn_${uniqueId}`;
    }
    if (imagePreview) {
      imagePreview.id = `inlineImagePreview_${uniqueId}`;
    }
    if (selectImageSize) {
      selectImageSize.id = `inlineSelectImageSize_${uniqueId}`;
    }
    if (addImageBtn) {
      addImageBtn.id = `inlineAddImageBtn_${uniqueId}`;
    }

    placeholderContainer.appendChild(inlineForm);
    canvasCharts.appendChild(placeholderContainer);

    // Initialize form handlers for this instance
    initInlineImageFormInstance(inlineForm, placeholderContainer);

    // Scroll to the new placeholder
    placeholderContainer.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });

    // Reset flag after a short delay
    setTimeout(function () {
      isAddingImagePlaceholder = false;
    }, 100);
  }

  /**
   * Initialize inline image form instance
   */
  function initInlineImageFormInstance(formElement, placeholderContainer) {
    const imageIdInput = formElement.querySelector("input.image-id-input");
    const imageSelectBtn = formElement.querySelector("button.upload-image-button");
    const imagePreview = formElement.querySelector("div.image-preview");
    const previewImg = imagePreview?.querySelector("img.preview-img");
    const removeImageBtn = imagePreview?.querySelector("button.btn-remove-image");
    const selectImageSize = formElement.querySelector("select[id^='inlineSelectImageSize']");
    const addImageBtn = formElement.querySelector("button[id^='inlineAddImageBtn']");
    const cancelButtons = formElement.querySelectorAll(".btn-cancel-image-config");

    if (!imageIdInput || !imageSelectBtn || !selectImageSize || !addImageBtn) {
      return;
    }

    // Handle image selection button
    imageSelectBtn.addEventListener("click", function () {
      const frame = initMediaFrame();
      if (!frame) {
        alert("WordPress media library is not available. Please refresh the page.");
        return;
      }

      // Set selected image if one is already chosen
      frame.on("open", function () {
        const selectedId = imageIdInput.value;
        if (selectedId) {
          const selection = frame.state().get("selection");
          selection.add(wp.media.attachment(selectedId));
        }
      });

      // When an image is selected, run a callback
      frame.on("select", function () {
        const attachment = frame.state().get("selection").first().toJSON();

        // Store image ID
        imageIdInput.value = attachment.id;

        // Show preview
        if (imagePreview && previewImg) {
          previewImg.src = attachment.sizes?.thumbnail?.url || attachment.url;
          previewImg.alt = attachment.alt || "Image preview";
          imagePreview.style.display = "block";
        }

        // Enable size selection
        selectImageSize.disabled = false;
        selectImageSize.value = ""; // Reset size selection

        // Update button state
        updateAddImageButtonState(imageIdInput, selectImageSize, addImageBtn);
      });

      // Open the modal
      frame.open();
    });

    // Handle remove image button
    if (removeImageBtn) {
      removeImageBtn.addEventListener("click", function () {
        imageIdInput.value = "";
        if (imagePreview) {
          imagePreview.style.display = "none";
        }
        selectImageSize.disabled = true;
        selectImageSize.value = "";
        updateAddImageButtonState(imageIdInput, selectImageSize, addImageBtn);
      });
    }

    // Handle image size selection
    selectImageSize.addEventListener("change", function () {
      updateAddImageButtonState(imageIdInput, selectImageSize, addImageBtn);
    });

    // Handle add image button
    addImageBtn.addEventListener("click", function () {
      addImageFromInlineFormInstance(formElement, placeholderContainer);
    });

    // Handle cancel buttons
    cancelButtons.forEach((btn) => {
      btn.addEventListener("click", function () {
        placeholderContainer.remove();

        // Show empty message if no items left
        const canvasCharts = window.ReportBuilderUtils?.getCanvasContainer();
        if (canvasCharts && canvasCharts.children.length === 0) {
          if (window.ReportBuilderUtils?.toggleEmptyMessage) {
            window.ReportBuilderUtils.toggleEmptyMessage(true);
          }
        }
      });
    });
  }

  /**
   * Update add image button state
   */
  function updateAddImageButtonState(imageIdInput, selectImageSize, addImageBtn) {
    const hasImage = imageIdInput.value !== "";
    const hasSize = selectImageSize.value !== "";

    addImageBtn.disabled = !(hasImage && hasSize);
  }

  /**
   * Add image from inline form instance
   */
  function addImageFromInlineFormInstance(formElement, placeholderContainer) {
    const imageIdInput = formElement.querySelector("input.image-id-input");
    const selectImageSize = formElement.querySelector("select[id^='inlineSelectImageSize']");

    if (!imageIdInput || !selectImageSize || !imageIdInput.value || !selectImageSize.value) {
      return;
    }

    const imageId = parseInt(imageIdInput.value);
    const imageSize = selectImageSize.value;

    // Create unique image ID
    const imageItemId = `report_image_${++imageCounter}_${Date.now()}`;

    // Create image configuration
    const imageConfig = {
      id: imageItemId,
      imageId: imageId,
      imageSize: imageSize,
      customTitle: null, // Will default to image filename if null
    };

    // Add to placed images
    placedImages.push(imageConfig);

    // Replace placeholder with image
    renderImageInCanvas(imageConfig, placeholderContainer);
  }

  /**
   * Render image in canvas
   */
  function renderImageInCanvas(imageConfig, placeholder = null) {
    const canvasCharts = window.ReportBuilderUtils?.getCanvasContainer();
    if (!canvasCharts) {
      return;
    }

    // Create image container
    const imageContainer = document.createElement("div");
    imageContainer.className = "canvas-image-item";
    imageContainer.id = `image_container_${imageConfig.id}`;
    imageContainer.setAttribute("data-image-id", imageConfig.id);

    // Create image header with controls
    const imageHeader = document.createElement("div");
    imageHeader.className = "image-item-header";
    const displayTitle = imageConfig.customTitle || "Image";

    if (window.ReportBuilderUtils?.generateItemHeaderHtml) {
      imageHeader.innerHTML = window.ReportBuilderUtils.generateItemHeaderHtml({
        itemId: imageConfig.id,
        displayTitle: displayTitle,
        idAttribute: "data-image-id",
        itemType: "image",
        removeButtonClass: "btn-remove-image",
        removeButtonTitle: "Remove image",
      });
    } else {
      // Fallback if utils not available
      imageHeader.innerHTML = `<div class="image-item-title"><h4>${displayTitle}</h4></div>`;
    }

    // Create image content area
    const imageContent = document.createElement("div");
    imageContent.className = "image-item-content";
    imageContent.id = `image_content_${imageConfig.id}`;
    imageContent.innerHTML = '<div class="image-loading">Loading image...</div>';

    // Assemble image container
    imageContainer.appendChild(imageHeader);
    imageContainer.appendChild(imageContent);

    // Replace placeholder or add to canvas
    if (placeholder && placeholder.parentNode) {
      placeholder.parentNode.replaceChild(imageContainer, placeholder);
    } else {
      canvasCharts.appendChild(imageContainer);
    }

    // Setup move buttons
    if (window.ReportBuilderUtils?.setupMoveButtons) {
      window.ReportBuilderUtils.setupMoveButtons(
        imageContainer,
        imageConfig.id,
        "data-image-id",
        function () {
          updateImageArrayOrder(imageConfig.id);
        }
      );
    }

    // Setup edit title button
    if (window.ReportBuilderUtils?.setupEditTitleButton) {
      window.ReportBuilderUtils.setupEditTitleButton(
        imageContainer,
        imageConfig.id,
        "image",
        function (newTitle) {
          imageConfig.customTitle = newTitle;
        }
      );
    }

    // Setup remove button
    const removeBtn = imageContainer.querySelector(".btn-remove-image");
    if (removeBtn) {
      removeBtn.addEventListener("click", function () {
        if (window.ReportBuilderUtils?.showConfirmationModal) {
          window.ReportBuilderUtils.showConfirmationModal({
            title: "Remove Image",
            message: "Are you sure you want to remove this image? This action cannot be undone.",
            confirmText: "Remove",
            cancelText: "Cancel",
            onConfirm: function() {
              removeImage(imageConfig.id);
            }
          });
        } else {
          // Fallback if modal not available
          if (confirm("Are you sure you want to remove this image?")) {
            removeImage(imageConfig.id);
          }
        }
      });
    }

    // Load image via AJAX
    loadImageViaAjax(imageConfig);
  }

  /**
   * Load image via AJAX
   */
  async function loadImageViaAjax(imageConfig) {
    const imageContent = document.getElementById(`image_content_${imageConfig.id}`);
    if (!imageContent) {
      return;
    }

    // Show loading state
    imageContent.innerHTML = '<div class="image-loading">Loading image...</div>';

    try {
      // Check if report builder data exists
      if (!window.reportBuilderData) {
        throw new Error(
          "Report builder data not loaded. Please refresh the page."
        );
      }

      // Get AJAX URL from report builder data
      const ajaxUrl = window.reportBuilderData.ajaxurl || "/wp-admin/admin-ajax.php";

      // Check if nonce exists
      if (!window.reportBuilderData.nonce) {
        throw new Error(
          "Security nonce not found. Please refresh the page."
        );
      }

      // Make AJAX request to get image URL
      const formData = new FormData();
      formData.append("action", "get_image_url");
      formData.append("image_id", imageConfig.imageId);
      formData.append("size", imageConfig.imageSize);
      formData.append("nonce", window.reportBuilderData.nonce);

      const response = await fetch(ajaxUrl, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("AJAX response error:", response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        const errorMsg =
          result.data?.message || "Failed to load image";
        console.error("Image load failed:", errorMsg);
        throw new Error(errorMsg);
      }

      // Get image URL and alt text
      const imageUrl = result.data.url || "";
      const imageAlt = result.data.alt || "";
      const imageTitle = result.data.title || "";

      // Update image config with title if available
      if (imageTitle && !imageConfig.customTitle) {
        imageConfig.customTitle = imageTitle;
        // Update header title
        const titleElement = document.querySelector(
          `.image-item-title .title-text[data-image-id="${imageConfig.id}"]`
        );
        if (titleElement) {
          const escapeHtml = window.ReportBuilderUtils?.escapeHtml || function(text) {
            const div = document.createElement("div");
            div.textContent = text;
            return div.innerHTML;
          };
          titleElement.textContent = escapeHtml(imageTitle);
        }
      }

      // Insert image HTML
      const escapeHtml = window.ReportBuilderUtils?.escapeHtml || function(text) {
        const div = document.createElement("div");
        div.textContent = text;
        return div.innerHTML;
      };

      imageContent.innerHTML = `
        <div class="image-wrapper">
          <img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(imageAlt)}" class="report-image" />
        </div>
      `;
    } catch (error) {
      console.error("Error loading image:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        imageConfig: imageConfig,
      });

      const escapeHtml = window.ReportBuilderUtils?.escapeHtml || function(text) {
        const div = document.createElement("div");
        div.textContent = text;
        return div.innerHTML;
      };

      imageContent.innerHTML = `
        <div class="image-error">
          <p>Error loading image: ${escapeHtml(error.message)}</p>
          <p style="font-size: 0.875rem; color: #666; margin-top: 0.5rem;">Check the browser console for more details.</p>
          <button class="btn-retry-image" data-image-id="${imageConfig.id}">Retry</button>
        </div>
      `;

      // Setup retry button
      const retryBtn = imageContent.querySelector(".btn-retry-image");
      if (retryBtn) {
        retryBtn.addEventListener("click", function () {
          loadImageViaAjax(imageConfig);
        });
      }
    }
  }

  /**
   * Update image array order to match DOM order
   */
  function updateImageArrayOrder(imageId) {
    if (window.ReportBuilderUtils?.updateArrayOrder) {
      window.ReportBuilderUtils.updateArrayOrder(
        placedImages,
        imageId,
        "image_container_",
        "data-image-id"
      );
    }
  }

  /**
   * Remove image from canvas
   */
  function removeImage(imageId) {
    // Remove from placed images array
    placedImages = placedImages.filter((i) => i.id !== imageId);

    // Remove from DOM
    const imageContainer = document.getElementById(`image_container_${imageId}`);
    if (imageContainer) {
      imageContainer.remove();
    }

    // Show empty message if no items left
    const canvasCharts = window.ReportBuilderUtils?.getCanvasContainer();
    if (canvasCharts && canvasCharts.children.length === 0) {
      if (window.ReportBuilderUtils?.toggleEmptyMessage) {
        window.ReportBuilderUtils.toggleEmptyMessage(true);
      }
    }
  }

  // Export functions for external use
  window.ReportBuilderImage = {
    addImagePlaceholder: addImagePlaceholder,
    removeImage: removeImage,
  };

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initPlaceImage);
  } else {
    initPlaceImage();
  }
})();

